<template>
  <div class="theme-list__wrapper">
    <p class="theme-list__text-wrapper" v-if="localText && !selectedTextModalResponseId">
      <VisibleText>Select a theme to add </VisibleText>
      <span :class="['theme-list__text-input', type]">{{ localText }}</span>
      {{ type }}.
    </p>
    <p class="theme-list__text-wrapper" v-if="selectedTextModalResponseId">
      <VisibleText>Select a theme to add this response to</VisibleText>
    </p>
    <span class="theme-list__message">
      {{ message }}
    </span>
    <div class="theme-searchbar__wrapper">
      <div class="theme-searchbar">
        <SvgIconDecorative icon="search" class="theme-searchbar__icon" />
        <input
          type="text"
          class="theme-searchbar__input"
          placeholder="Search themes..."
          v-model.trim="searchQuery"
        />
      </div>
    </div>

    <ul class="theme-list__rows">
      <li
        v-for="(theme, index) in paginatedThemes.filter((t) => t != null)"
        :key="theme._id || index"
        class="theme-list__row"
      >
        <VisibleText v-if="localText || selectedTextModalResponseId">Add here</VisibleText>
        <input
          :aria-label="theme.name"
          class="theme-list__row-radio-input"
          type="radio"
          :value="index"
          :disabled="themeContains(index, localText)"
          v-show="localText || selectedTextModalResponseId"
          @change="changingSelection(theme._id)"
        />
        <div
          :class="[
            'theme-list__row-content',
            {
              selected: selectedNewThemeUUID === theme._id,
              disabled: themeContains(index, localText),
            },
          ]"
          :style="theme.is_proposed ? 'background-color: var(--squeeze-blue)' : ''"
          style="grid-template-columns: 20% 70% 4% 4%; width: 90%"
        >
          <div class="title">
            <VisibleText
              v-if="theme.is_proposed"
              style="font-size: 0.8rem; color: var(--primary); font-style: italic"
              >new theme</VisibleText
            >
            <InputEditable
              :ref="'input-editable-' + index"
              :value="theme.name"
              :isFocused="shouldFocusFirstTheme && index == 0"
              :selectHandler="() => {}"
              @updateInput="$emit('update-theme-title', { index: index, title: $event })"
            />
            <div
              v-if="duplicateIndex == index"
              style="font-size: 0.7rem; color: var(--state-invalid-colour)"
            >
              <VisibleText>No duplicate titles allowed.</VisibleText>
            </div>
            <ThemeSource :theme="theme" parent="generated"></ThemeSource>
            <button
              v-if="isAutoGenerated && !isListForSaving"
              class="table__toolbar-btn"
              style="font-size: 0.8em; padding: 0.3em 0.6em 0.3em 0.3em; margin-top: 0.5em"
              :disabled="spinner.isLoading.filter((spinner) => spinner._id == theme._id)[0]"
              @click="addToListForSaving(theme)"
            >
              <SvgIconDecorative icon="add" />
              <VisibleText>Keep this theme</VisibleText>
            </button>
            <button
              v-if="isAutoGenerated && isListForSaving"
              class="table__toolbar-btn"
              style="font-size: 0.8em; padding: 0.3em; margin-top: 0.5em"
              @click="removeFromListForSaving(theme)"
            >
              <SvgIconDecorative icon="minus" />
              <VisibleText>Remove</VisibleText>
            </button>
          </div>
          <div style="display: flex; flex-direction: column">
            <div>
              <KeywordsInput
                v-if="type == 'keyword'"
                :textAnalysisObject="textAnalysisObject"
                :hasPreProcessedText="hasPreProcessedText"
                :existingTheme="theme"
                class="ki-wrapper"
                :value="allTags.filter((q) => q.key == index)"
                :validate="(el) => !isBannedKeyword(el, true)"
                :text="selected === index ? localText : ''"
                @tag-added="addKeyword(index, $event)"
                @tag-removed="removeKeyword(index, $event)"
                @banKeyword="saveBanKeyword($event)"
              />
            </div>

            <div v-if="theme.notes && theme.notes.length">
              <NotesPopup
                :clientQuestionId="textAnalysisObject.selectedTextQuestion._id"
                :themesLength="sortedThemes.length"
                :theme="theme"
                :themeIndex="index"
                :allResponses="responses"
                :hasPreProcessedText="hasPreProcessedText"
                :isThemeList="true"
                @onRemoveNote="onRemoveNote($event)"
                @selectThisResponse="selectThisResponse($event)"
                @addNewNote="addNewNote($event)"
                @updateTheme="this.$emit('updateTheme', $event)"
                @refreshTheme="this.$emit('refreshTheme', $event)"
                :showCoverageDetails="textAnalysisObject.showCoverageDetails"
                style="
                  padding: 0.2em;
                  background: #fff;
                  border: 1px solid transparent;
                  border-top-color: transparent;
                  border-right-color: transparent;
                  border-bottom-color: transparent;
                  border-left-color: transparent;
                  border-radius: 0.25em;
                  border-color: var(--outline-colour);
                "
              />
            </div>
          </div>
          <div class="theme__list-row-coverage" style="width: 100%; display: flex">
            <div
              class="theme__list-row-coverage-title"
              style="display: flex; flex-direction: row"
              v-if="!spinner.isLoading.filter((spinner) => spinner._id == theme._id)[0]"
            >
              <CoveragePopup
                :index="index"
                :theme="theme"
                style="display: flex"
                :isThemelist="true"
                :hasPreProcessedText="hasPreProcessedText"
                :includeSmartCoverage="includeSmartCoverage"
                :sortedResponsesLength="responses.length"
                :hasFilter="
                  Boolean(
                    textAnalysisObject.filterParams &&
                    Object.keys(textAnalysisObject.filterParams).length,
                  )
                "
              />
            </div>
            <div
              v-if="spinner.isLoading.filter((spinner) => spinner._id == theme._id)[0]"
              style="width: 100%"
              :style="
                spinner.isLoading.filter((spinner) => spinner._id == theme._id)[0]
                  ? 'color: grey;'
                  : ''
              "
            ></div>
          </div>
          <div style="width: 2rem">
            <div v-if="!propReadOnly">
              <ButtonIconOnly
                v-if="!isListForSaving"
                style="margin: 0"
                icon="deleteIcon"
                @click-handler="deleteTheme(index)"
              >
                <VisibleText>delete theme </VisibleText>
              </ButtonIconOnly>
            </div>
            <div v-else>
              <SvgIconMeaningful
                icon="deleteIcon"
                style="width: 1em; cursor: not-allowed"
                title="Read only project"
              />
            </div>
          </div>
        </div>
      </li>
    </ul>
    <div
      v-if="paginatedThemes.length < localThemes.length"
      class="suggested-themes__options"
      style="display: flex; flex-direction: column"
    >
      <div class="theme-list__count" v-if="filteredThemes.length">
        <template v-if="!searchQuery">
          <VisibleText>We have found </VisibleText>
          <span>{{ localThemes.length }}</span>
          <VisibleText> themes but are only showing </VisibleText>
          <span>{{ paginatedThemes.length }}</span>
          <VisibleText>.</VisibleText>
        </template>

        <template v-else>
          <VisibleText>Found </VisibleText>
          <span>{{ filteredThemes.length }}</span>
          <VisibleText> matching themes. Showing </VisibleText>
          <span>{{ paginatedThemes.length }}</span>
          <VisibleText> of </VisibleText>
          <span>{{ filteredThemes.length }}</span>
          <VisibleText>.</VisibleText>
        </template>
      </div>

      <div
        style="display: flex; flex-direction: row"
        v-if="filteredThemes.length > paginatedThemes.length"
      >
        <button
          v-if="!parsedSpinner.length"
          class="btn-secondary"
          role="button"
          @click="onClickLoadMoreQuestions"
        >
          <VisibleText>Click to show more themes</VisibleText>
        </button>

        <div
          v-else
          class="btn-secondary"
          style="font-size: 0.8rem; color: var(--base-colour-light)"
        >
          <VisibleText>Wait for coverage to load then click here</VisibleText>
        </div>

        <div
          v-if="parsedSpinner.length"
          class="btn-secondary"
          style="font-size: 0.8rem; color: var(--base-colour-light)"
        >
          <VisibleText>Wait for coverage to load then click here</VisibleText>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import ButtonIconOnly from '@/components/UI/Button/ButtonIconOnly.vue';
import InputEditable from '../UI/InputEditable.vue';
import KeywordsInput from '../Components/KeywordsInput.vue';
import ThemeSource from '../Components/ThemeSource.vue';
import SvgIconDecorative from '@/components/UI/Svg/SvgIconDecorative.vue';
import SvgIconMeaningful from '@/components/UI/Svg/SvgIconMeaningful.vue';
import CoveragePopup from '@/components/Project/ProjectAnalysis/ProjectAnalysisText/Components/CoveragePopup.vue';
import NotesPopup from '@/components/Project/ProjectAnalysis/ProjectAnalysisText/Components/NotesPopup.vue';

// Mixins
import textAnalysisMixin from '@/components/Project/ProjectAnalysis/ProjectAnalysisText/Mixins/textAnalysisMixin.js';
import themeColorsMixin from './../../Mixins/themeColorsMixin.js';

export default {
  name: 'ThemeList',
  props: {
    type: {
      default: () => 'keyword',
      type: String,
      required: true,
      validator: function (value) {
        return ['keyword', 'respondent'].indexOf(value) !== -1;
      },
    },
    message: {
      type: String,
      default: function () {
        return '';
      },
    },
    // Can we move the below to the single item prop?
    themes: {
      type: Array,
      required: true,
    },
    sortedThemes: {
      type: Array,
      default: () => [],
    },
    selectedNewThemeUUID: {
      type: String,
      default: '',
    },
    selection: {
      type: Object,
      required: false,
      default: () => {},
    },
    shouldFocusFirstTheme: {
      type: Boolean,
      default: function () {
        return false;
      },
    },
    isListForSaving: {
      type: Boolean,
      default: () => false,
    },
    isAutoGenerated: {
      type: Boolean,
      default: () => false,
    },
    textAnalysisObject: {
      type: Object,
      required: false,
      default: () => {},
    },
    responses: {
      type: Array,
      default: () => [],
    },
    selectedTextModalResponseId: {
      type: String,
      required: false,
      default: () => '',
    },
    duplicateIndex: {
      type: Number,
      required: false,
      default: () => -1,
    },
    refreshCoverage: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    hasPreProcessedText: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeSmartCoverage: {
      type: String,
      default: () => 'exclude',
    },
    isThemeEdit: {
      type: Boolean,
      default: () => false,
    },
    propReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  components: {
    ButtonIconOnly,
    InputEditable,
    KeywordsInput,
    ThemeSource,
    SvgIconDecorative,
    CoveragePopup,
    SvgIconMeaningful,
    NotesPopup,
  },
  mixins: [textAnalysisMixin, themeColorsMixin],
  data() {
    return {
      selected: -1,
      deleteThisUUID: '',
      localThemes: [],
      localText: '',
      spinner: { isLoading: [] },
      hasBeenEdited: [],
      changeCount: 0,
      lastInListForSaving: [],
      pagination: {
        endIndx: 5,
        limit: 5, //limit of how many are shown in a page
        startIndx: 0,
      },
      searchQuery: '',
    };
  },
  computed: {
    allTags() {
      const allTags = [];
      const len = this.paginatedThemes.length;

      for (let i = 0; i < len; i++) {
        const theme = this.paginatedThemes[i];
        const keywords = this.normalizeKeywords(theme);

        for (let x = 0; x < keywords.length; x++) {
          const kw = keywords[x];
          if (!this.isBannedKeyword(kw)) {
            allTags.push({ key: i, value: kw });
          }
        }
      }

      return allTags;
    },
    parsedSpinner: function () {
      if (this.spinner.isLoading) {
        return this.spinner.isLoading;
      }
      return [];
    },
    bannedKeywords: function () {
      return this.textAnalysisObject.bannedKeywords;
    },

    filteredThemes() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.localThemes;

      // Single pass, no extra maps/filters beyond what’s needed
      return this.localThemes.filter((theme) => {
        const name = (theme.name || '').toLowerCase();
        if (name.includes(q)) return true;

        const keywords = this.normalizeKeywords(theme);
        for (let i = 0; i < keywords.length; i++) {
          if ((keywords[i] || '').toLowerCase().includes(q)) return true;
        }
        return false;
      });
    },
    paginatedThemes() {
      const start = this.pagination.startIndx;
      const end = this.pagination.endIndx;
      return this.filteredThemes.slice(start, end);
    },
  },
  created() {
    if (this.selection) {
      this.localText = this.trimContent(this.selection.content);
    }

    if (this.selectedNewThemeUUID) {
      this.selected = this.selectedNewThemeUUID;
    }
    this.localThemes = this.themes;
  },
  methods: {
    normalizeKeywords(theme) {
      if (!theme) return [];
      if (Array.isArray(theme.keywords)) return theme.keywords;
      if (Array.isArray(theme.keyword_objects)) {
        return theme.keyword_objects.map((ko) => ko.keyword);
      }
      return [];
    },
    updateTextObject(cleanedProject) {
      let object = {
        project: cleanedProject,
        scroll: 'main',
      };
      this.$emit('updateTextObject', object);
    },
    onClickLoadMoreQuestions() {
      this.pagination.endIndx = this.pagination.endIndx + this.pagination.limit;
    },
    /* Utils */
    deepCloneObj(obj) {
      // deep clones an object using JSON stringify (data loss might occur)
      if (Array.isArray(obj)) {
        return obj.map((item) => JSON.parse(JSON.stringify(item)));
      } else if (typeof obj == 'object') {
        return JSON.parse(JSON.stringify(obj));
      }
    },
    generateSentimentText(score) {
      return score >= 0
        ? `${(score * 100).toFixed(1)}% positive sentiment`
        : `${(score * -100).toFixed(1)}% negative sentiment`;
    },
    isBannedKeyword(keyword, warnIfTrue = false) {
      const val = this.bannedKeywords.indexOf(keyword.trim().toLowerCase()) !== -1;
      if (val && warnIfTrue) {
        alert(`Can't add \`${keyword}\`. REASON - banned keyword.`);
      }
      return val;
    },
    taggable(keywords) {
      return keywords
        .filter((el) => !this.isBannedKeyword(el))
        .map(function (text, index) {
          return { key: index, value: text };
        });
    },
    themeContains(index, text) {
      if (this.type === 'respondent') return false;
      const theme = this.localThemes[index];
      if (!theme || !text) return false;

      const keywords = this.normalizeKeywords(theme);
      return keywords.includes(text);
    },

    // This is used by ThemesAdd with a radio
    changingSelection(_id) {
      if (this.type === 'keyword') {
        this.selected = _id;
        // The selected watcher will handle patch creation.
      }
    },

    allThemeContains(text) {
      return this.localThemes.every((_, index) => this.themeContains(index, text));
    },
    addToArray(el, array) {
      if (array.indexOf(el) !== -1) return;
      array.push(el);
    },
    removeFromArray(el, array) {
      if (array.indexOf(el) === -1) return;
      array.splice(array.indexOf(el), 1);
    },

    /* Theme methods */
    addKeyword(index, text) {
      const theme = this.localThemes[index];
      if (!theme) return;

      const themeId = theme._id.$oid || theme._id;
      const keyword = text;

      this.hasBeenEdited.push({
        _id: themeId,
        keywords_to_add: [keyword],
      });
      this.changeCount++;
      this.$emit('updated', true);
    },

    removeKeyword(index, text) {
      const theme = this.localThemes[index];
      if (!theme) return;

      const themeId = theme._id.$oid || theme._id;
      const keyword = text;

      this.hasBeenEdited.push({
        _id: themeId,
        keywords_to_remove: [keyword],
      });

      this.changeCount++;
      this.$emit('updated', true);
    },

    addNote(index, noteObject) {
      const theme = this.localThemes[index];
      if (!theme) return;

      const themeId = theme._id.$oid || theme._id;

      this.hasBeenEdited.push({
        _id: themeId,
        notes_to_add: [noteObject],
      });

      this.changeCount++;
      this.$emit('updated', true);
    },

    removeNote(index, responseId) {
      const theme = this.localThemes[index];
      if (!theme) return;

      const themeId = theme._id.$oid || theme._id;

      this.hasBeenEdited.push({
        _id: themeId,
        notes_to_remove: [responseId],
      });

      this.changeCount++;
      this.$emit('updated', true);
    },

    ignoreTheme(index, responseId) {
      const theme = this.localThemes[index];
      if (!theme) return;

      const themeId = theme._id.$oid || theme._id;

      this.hasBeenEdited.push({
        _id: themeId,
        dropped_to_add: [responseId],
      });

      this.changeCount++;
      this.$emit('updated', true);
    },

    unignoreTheme(index, responseId) {
      const theme = this.localThemes[index];
      if (!theme) return;

      const themeId = theme._id.$oid || theme._id;

      this.hasBeenEdited.push({
        _id: themeId,
        dropped_to_remove: [responseId],
      });

      this.changeCount++;
      this.$emit('updated', true);
    },

    deleteTheme: async function (index) {
      let this_id = this.localThemes[index]._id;
      if (this_id.$oid) {
        this_id = this.localThemes[index]._id.$oid;
      }
      this.deleteThisUUID = this_id;
      this.onClickDelete();
      this.setConfirmStatus(false);
    },
    onClickDelete() {
      this.setConfirmText({
        btn: 'Yes',
        title: 'Confirm Delete',
        message: 'This theme has been previously saved. Are you sure you want to ' + 'delete it?',
      });
      this.setConfirmSourceComponent('themeList');
      this.setConfirmType('delete');
      this.setConfirmTarget(this.deleteThisUUID);
      this.setConfirmIsVisible(true);
      this.setConfirmStatus(true);
    },
    confirmedDelete: async function () {
      let thisTheme = this.localThemes.filter(
        (item) =>
          item._id == this.deleteThisUUID ||
          (item._id.$oid && item._id.$oid == this.deleteThisUUID),
      );

      let skipSave = false;
      if (thisTheme[0] && thisTheme[0].is_proposed) {
        skipSave = true;
      }
      if (thisTheme[0] && thisTheme[0].is_autogenerated) {
        skipSave = true;
      }
      if (skipSave == false) {
        await this.saveDeletedTheme(this.deleteThisUUID);
      }
      this.localThemes = this.localThemes.filter((item) => item._id !== this.deleteThisUUID);
      this.$emit('removeTheme', this.deleteThisUUID);

      this.setConfirmTarget(null);
      this.deleteThisUUID = '';
      this.$forceUpdate();
      this.$emit('updated', true);
    },
    async addToListForSaving(thisTheme) {
      this.$emit('addToListForSaving', thisTheme);
    },
    removeFromListForSaving(thisTheme) {
      this.$emit('removeFromListForSaving', thisTheme);
      const clonedThemes = this.deepCloneObj(this.localThemes);
      for (let i = 0; i < clonedThemes.length; i++) {
        if (this.lastInListForSaving.includes(clonedThemes[i]._id)) {
          this.lastInListForSaving.splice(i, 1);
        }
      }
    },
    refreshThemesFromParent() {
      this.localThemes = this.themes;
    },

    trimContent(content) {
      return content.replace(/[.,!;?\)\("]+$/g, '');
    },
  },
  // This checks for old themes and refreshes them automically.
  watch: {
    themes: function (val) {
      if (val) {
        this.localThemes = val;
      }
    },
    parsedSpinner: function (val) {
      this.$emit('updatingSpinner', val);
    },
    changeCount(val, oldVal) {
      if (val > 0) {
        setTimeout(() => {
          if (this.changeCount > 0) {
            this.changeCount--;
          }
        }, 10);
      }

      if (val === 0 && oldVal > 0) {
        // When all edits settle, send patches
        if (this.hasBeenEdited.length > 0) {
          const patches = this.deepCloneObj(this.hasBeenEdited);

          // Clear before emitting to avoid duplicates
          this.hasBeenEdited = [];
          // Emit patches upward to parent
          this.$emit('applyPatches', patches);
        }

        this.changeCount = 0;
      }

      if (val < 0) {
        this.changeCount = 0;
      }
    },

    selected(val) {
      if (!val) return;
      if (!this.selection || !this.selection.content) return;

      const keyword = this.trimContent(this.selection.content);
      if (!keyword) return;

      const selectedThemeId = this.selected.$oid || this.selected;

      // Add keyword to selected theme
      this.hasBeenEdited.push({
        _id: selectedThemeId,
        keywords_to_add: [keyword],
      });

      // Remove keyword from all other themes
      this.localThemes.forEach((theme) => {
        if (theme._id === selectedThemeId) return;

        const hasKeyword =
          theme.keyword_objects &&
          theme.keyword_objects.some(
            (ko) => ko.keyword.trim().toLowerCase() === keyword.toLowerCase(),
          );

        if (hasKeyword) {
          this.hasBeenEdited.push({
            _id: theme._id,
            keywords_to_remove: [keyword],
          });
        }
      });

      this.changeCount++;
      this.$emit('updated', true);
    },

    confirmStatus: async function (val) {
      if (
        val &&
        this.confirmSourceComponent === 'themeList' &&
        this.confirmType === 'delete' &&
        this.confirmTarget === this.deleteThisUUID
      ) {
        await this.confirmedDelete();
      }
    },

    searchQuery() {
      this.pagination.startIndx = 0;
      this.pagination.endIndx = this.pagination.limit;
    },
  },
};
</script>

<style scoped>
.theme-searchbar__wrapper {
  width: 100%;
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
}

.theme-searchbar {
  width: 100%;
  max-width: 420px;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid var(--outline-colour);
  border-radius: 0.5rem;
  padding: 0.45rem 0.75rem;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.theme-searchbar:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.15);
}

.theme-searchbar__icon {
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
  opacity: 0.6;
}

.theme-searchbar__input {
  width: 100%;
  border: none;
  outline: none;
  font-size: 0.95rem;
  background: transparent;
  color: var(--base-colour);
  margin: 0;
}

.theme-searchbar__input::placeholder {
  color: var(--base-colour-light);
}
</style>
